/**
 * Journal.tsx — /app/journal  (v8.4)
 *
 * Review, learning, attribution, session intelligence.
 * Tabs: Trade Log | Audit | Attribution | Calibration
 */
import React, { useState, useMemo } from 'react';
import { useStore }         from '../../store';
import { JournalPanel }     from '../../features/journal/JournalPanel';
import { AuditPanel }       from '../../features/audit/AuditPanel';
import { loadSnapshotsForSymbol } from '../../research/researchSnapshot';
import {
  evaluateSession,
  type OutcomeValidationResult,
  type SessionAttributionSummary,
} from '../../domains/replay/outcomeValidation';
import {
  buildAdaptiveLearningProfile,
  AdaptiveLearningProfile,
  ADAPTIVE_LEARNING_THRESHOLDS as AL,
} from '../../domains/learning/adaptiveLearning';
import {
  isActiveAdaptationEnabled, buildAppliedModifierState,
  type AppliedModifier,
} from '../../domains/learning/adaptiveApplication';
import {
  getGovernanceSnapshot,
  setGovernanceMode,
  setRequireApproval,
  freeze, unfreeze,
  approveProposal, rejectProposal,
  rollbackLast, rollbackSignal, rollbackAll,
  proposeFromLearningProfile,
  type GovernanceSnapshot,
} from '../../domains/learning/adaptiveGovernance';
import {
  evaluateExecution, summarizeExecutionFeedback,
  type ExecutionFeedback,
} from '../../domains/execution/executionFeedback';
import { buildExecutionPlan } from '../../domains/execution/executionIntelligence';
import { getSymbolContext }   from '../../selectors/symbolContext';
import { getThesis }          from '../../ai/thesisEngine';
import {
  buildSessionIntelligenceSummary,
  type SessionIntelligenceSummary,
} from '../../domains/operator/operatorIntelligence';
import { buildCoachingReport, type CoachingReport } from '../../domains/operator/operatorCoaching';
import { buildComparativeReport, type ComparativeReport } from '../../domains/operator/operatorComparative';
import { analyseReplayHistory } from '../../domains/replay/replayIntelligence';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  gold:   '#C9A84C', border: '#1A3050', card:   'rgba(13,31,53,0.55)',
  text:   '#E8EDF5', dim:    '#4A6FA5', muted:  '#3A5070',
  green:  '#4ADE80', red:    '#F87171', amber:  '#FCD34D',
  mono:   "'Courier New', monospace",
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function outcomeColor(q: string): string {
  if (q === 'strong_win') return C.green;
  if (q === 'weak_win')   return '#86EFAC';
  if (q === 'breakeven')  return C.dim;
  if (q === 'weak_loss')  return '#FCA5A5';
  if (q === 'strong_loss')return C.red;
  return C.muted;
}

function calibColor(r: string): string {
  if (r === 'well_calibrated')  return C.green;
  if (r === 'overconfident')    return C.red;
  if (r === 'underconfident')   return C.amber;
  return C.muted;
}

function layerColor(layer: string): string {
  const map: Record<string, string> = {
    gamma: '#A78BFA', trap: C.red, structure: '#38BDF8',
    thesis: C.gold, volatility: C.amber, liquidity: '#34D399', mixed: C.dim,
  };
  return map[layer] ?? C.muted;
}

// ── Attribution tab ───────────────────────────────────────────────────────────

function AttributionTab({ evaluations, summary }: { evaluations: OutcomeValidationResult[]; summary: SessionAttributionSummary }) {
  if (evaluations.length === 0) return (
    <div style={{ color: C.muted, fontSize: 11, paddingTop: 40, textAlign: 'center' }}>
      No evaluated snapshots. Capture research snapshots and mark outcomes to generate attribution data.
    </div>
  );

  const actedOn = evaluations.filter(e => e.decisionStatus === 'worked' || e.decisionStatus === 'failed' || e.decisionStatus === 'acted_on');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 760 }}>
      {/* Summary header */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
        {[
          { label: 'EVALUATIONS', value: `${summary.evaluationCount}`, color: C.text },
          { label: 'WIN RATE',    value: summary.winRate !== null ? `${(summary.winRate * 100).toFixed(0)}%` : '—', color: summary.winRate !== null && summary.winRate >= 0.55 ? C.green : C.red },
          { label: 'AVG CONV',   value: summary.avgConviction !== null ? `${summary.avgConviction}%` : '—', color: C.gold },
          { label: 'DOMINANT',   value: summary.dominantAttributionLayer, color: layerColor(summary.dominantAttributionLayer) },
        ].map(m => (
          <div key={m.label} style={{ padding: '12px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3, textAlign: 'center' }}>
            <div style={{ fontSize: 8, letterSpacing: 1.5, color: C.dim, marginBottom: 4 }}>{m.label}</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: m.color, fontFamily: C.mono, textTransform: 'uppercase' }}>{m.value}</div>
          </div>
        ))}
      </div>

      {/* Per-snapshot evaluations */}
      {actedOn.length > 0 && (
        <div style={{ padding: '14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
          <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 10 }}>ACTED-ON SNAPSHOT EVALUATIONS</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {actedOn.map((e, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${C.border}`, fontSize: 10 }}>
                <span style={{ color: C.dim, minWidth: 55, fontFamily: C.mono }}>{new Date(e.snapshotTs).toLocaleDateString()}</span>
                <span style={{ color: e.expectedDir === 'long' ? C.green : C.red, minWidth: 40, fontWeight: 700 }}>{e.expectedDir.toUpperCase()}</span>
                <span style={{ color: C.dim, minWidth: 40, fontFamily: C.mono }}>{e.conviction}%</span>
                <span style={{ color: outcomeColor(e.outcomeQuality), flex: 1 }}>{e.outcomeQuality.replace('_', ' ')}</span>
                <span style={{ color: layerColor(e.attribution.primaryDriver), minWidth: 70 }}>{e.attribution.primaryDriver}</span>
                <span style={{ color: e.rMultiple !== null ? (e.rMultiple > 0 ? C.green : C.red) : C.muted, fontFamily: C.mono, minWidth: 40 }}>
                  {e.rMultiple !== null ? `${e.rMultiple}R` : '—'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Attribution weight breakdown (best evaluation) */}
      {summary.bestEvaluation && (
        <div style={{ padding: '14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
          <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 10 }}>BEST EVALUATION — SIGNAL ATTRIBUTION</div>
          <div style={{ fontSize: 11, color: C.text, marginBottom: 10 }}>{summary.bestEvaluation.attribution.primaryNote}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {summary.bestEvaluation.attribution.weights.map((w, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ color: layerColor(w.layer), minWidth: 80, fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}>{w.layer}</span>
                <div style={{ flex: 1, height: 6, background: '#1A3050', borderRadius: 3, maxWidth: 200 }}>
                  <div style={{ width: `${w.weight * 100}%`, height: '100%', background: layerColor(w.layer), borderRadius: 3 }} />
                </div>
                <span style={{ color: C.muted, fontSize: 10, fontFamily: C.mono, minWidth: 35 }}>{(w.weight * 100).toFixed(0)}%</span>
                <span style={{ color: '#5A7FA8', fontSize: 10 }}>{w.explanation}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lessons */}
      <div style={{ padding: '14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
        <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 10 }}>ATTRIBUTION LESSONS</div>
        {summary.lessons.map((l, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, fontSize: 11, color: C.text, lineHeight: 1.6, marginBottom: 4 }}>
            <span style={{ color: C.gold, flexShrink: 0 }}>›</span>
            <span>{l}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Calibration tab ───────────────────────────────────────────────────────────

function CalibrationTab({ evaluations, summary }: { evaluations: OutcomeValidationResult[]; summary: SessionAttributionSummary }) {
  const withOutcome = evaluations.filter(e => e.calibration.calibrationResult !== 'insufficient_data');

  if (withOutcome.length === 0) return (
    <div style={{ color: C.muted, fontSize: 11, paddingTop: 40, textAlign: 'center' }}>
      No calibration data. Mark snapshot outcomes as worked/failed to enable calibration analysis.
    </div>
  );

  // Count calibration results
  const counts = { well_calibrated: 0, overconfident: 0, underconfident: 0 };
  for (const e of withOutcome) {
    const r = e.calibration.calibrationResult as keyof typeof counts;
    if (r in counts) counts[r]++;
  }
  const total = withOutcome.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 760 }}>
      {/* Summary bar */}
      <div style={{ padding: '14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
        <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 10 }}>
          CALIBRATION SUMMARY — {total} EVALUATED
        </div>
        <div style={{ display: 'flex', gap: 0, height: 12, borderRadius: 3, overflow: 'hidden', marginBottom: 10 }}>
          {counts.well_calibrated > 0 && <div style={{ flex: counts.well_calibrated, background: C.green }} />}
          {counts.overconfident   > 0 && <div style={{ flex: counts.overconfident,   background: C.red }} />}
          {counts.underconfident  > 0 && <div style={{ flex: counts.underconfident,  background: C.amber }} />}
        </div>
        <div style={{ display: 'flex', gap: 20, fontSize: 10 }}>
          <span><span style={{ color: C.green }}>{counts.well_calibrated}</span><span style={{ color: C.muted }}> well calibrated</span></span>
          <span><span style={{ color: C.red }}>{counts.overconfident}</span><span style={{ color: C.muted }}> overconfident</span></span>
          <span><span style={{ color: C.amber }}>{counts.underconfident}</span><span style={{ color: C.muted }}> underconfident</span></span>
        </div>
      </div>

      {/* Overall calibration badge */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 9, letterSpacing: 2, color: C.dim }}>OVERALL CALIBRATION:</span>
        <span style={{
          fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1,
          color: calibColor(summary.overallCalibration),
          border: `1px solid ${calibColor(summary.overallCalibration)}40`,
          borderRadius: 2, padding: '2px 8px',
        }}>
          {summary.overallCalibration.replace('_', ' ')}
        </span>
      </div>

      {/* Per-snapshot calibration detail */}
      <div style={{ padding: '14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
        <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 10 }}>SNAPSHOT CALIBRATION DETAIL</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {withOutcome.map((e, i) => (
            <div key={i} style={{ padding: '8px 10px', background: 'rgba(10,22,40,0.4)', border: `1px solid ${C.border}`, borderRadius: 3 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 4, fontSize: 10 }}>
                <span style={{ color: C.dim, minWidth: 55, fontFamily: C.mono }}>{new Date(e.snapshotTs).toLocaleDateString()}</span>
                <span style={{ color: C.gold, fontFamily: C.mono, minWidth: 40 }}>{e.conviction}%</span>
                <span style={{ color: calibColor(e.calibration.calibrationResult), fontWeight: 700, textTransform: 'uppercase', flex: 1 }}>
                  {e.calibration.calibrationResult.replace('_', ' ')}
                </span>
                <span style={{ color: outcomeColor(e.outcomeQuality) }}>{e.outcomeQuality.replace('_', ' ')}</span>
              </div>
              <div style={{ fontSize: 10, color: '#5A7FA8', lineHeight: 1.5 }}>{e.calibration.explanation}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Intelligence tab ─────────────────────────────────────────────────────────

function gradeColor(q: string): string {
  return q === 'high' ? C.green : q === 'low' ? C.red : q === 'medium' ? C.amber : C.muted;
}

function scoreColor(score: number): string {
  return score >= 80 ? C.green : score >= 65 ? C.amber : score >= 50 ? '#FCD34D' : C.red;
}

function IntelligenceTab({ intel }: { intel: SessionIntelligenceSummary }) {
  const s = intel.sessionScore;
  const gradeColors: Record<string, string> = { A: C.green, B: '#86EFAC', C: C.amber, D: C.red, F: '#F87171' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 760 }}>
      {/* Session score header */}
      <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 10 }}>
        {/* Grade badge */}
        <div style={{ padding: '16px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3, textAlign: 'center' }}>
          <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 6 }}>GRADE</div>
          <div style={{ fontSize: 36, fontWeight: 700, fontFamily: C.mono, color: gradeColors[s.grade] ?? C.muted }}>
            {s.grade === 'insufficient_data' ? '—' : s.grade}
          </div>
          {s.grade !== 'insufficient_data' && (
            <div style={{ fontSize: 11, color: scoreColor(s.overall), fontFamily: C.mono }}>{s.overall}/100</div>
          )}
        </div>
        {/* Component scores */}
        <div style={{ padding: '12px 14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
          <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 10 }}>SCORE COMPONENTS</div>
          {[
            { label: 'Win Rate',       val: s.components.winRate       },
            { label: 'Calibration',    val: s.components.calibration   },
            { label: 'Conviction',     val: s.components.conviction    },
            { label: 'Signal Clarity', val: s.components.signalClarity },
          ].map(({ label, val }) => (
            <div key={label} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 5 }}>
              <span style={{ color: C.muted, fontSize: 9, minWidth: 90 }}>{label}</span>
              <div style={{ flex: 1, height: 4, background: '#1A3050', borderRadius: 2 }}>
                <div style={{ width: `${val ?? 0}%`, height: '100%', background: val != null ? scoreColor(val) : C.border, borderRadius: 2 }} />
              </div>
              <span style={{ color: C.text, fontSize: 10, fontFamily: C.mono, minWidth: 30 }}>{val ?? '—'}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Best / worst calls */}
      {(intel.strongestCall || intel.weakestCall) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {[
            { label: 'BEST CALL', item: intel.strongestCall, accent: C.green },
            { label: 'WORST CALL', item: intel.weakestCall,  accent: C.red   },
          ].map(({ label, item, accent }) => item && (
            <div key={label} style={{ padding: '12px', background: C.card, border: `1px solid ${accent}30`, borderLeft: `3px solid ${accent}`, borderRadius: 3 }}>
              <div style={{ fontSize: 8, letterSpacing: 2, color: C.dim, marginBottom: 6 }}>{label}</div>
              <div style={{ fontSize: 11, color: C.text, marginBottom: 4 }}>{item.explanation}</div>
              <span style={{ fontSize: 9, color: gradeColor(item.quality), textTransform: 'uppercase', fontWeight: 700 }}>{item.quality}</span>
            </div>
          ))}
        </div>
      )}

      {/* Decision grades table */}
      {intel.decisionGrades.filter(g => g.quality !== 'unknown').length > 0 && (
        <div style={{ padding: '12px 14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
          <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 10 }}>DECISION GRADES</div>
          {intel.decisionGrades.filter(g => g.quality !== 'unknown').map((g, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '5px 0', borderBottom: `1px solid ${C.border}`, fontSize: 10 }}>
              <span style={{ color: gradeColor(g.quality), fontWeight: 700, minWidth: 50, textTransform: 'uppercase' }}>{g.quality}</span>
              <span style={{ color: g.calibrationFit === 'well_calibrated' ? C.green : g.calibrationFit === 'overconfident' ? C.red : C.muted, minWidth: 110 }}>
                {g.calibrationFit.replace('_', ' ')}
              </span>
              <span style={{ color: C.dim, minWidth: 60 }}>{g.primaryDriver}</span>
              <span style={{ color: C.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.explanation}</span>
            </div>
          ))}
        </div>
      )}

      {/* Pattern insights */}
      {intel.patternInsights.length > 0 && (
        <div style={{ padding: '12px 14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
          <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 10 }}>PATTERN INSIGHTS</div>
          {intel.patternInsights.map((p, i) => (
            <div key={i} style={{ padding: '6px 0', borderBottom: `1px solid ${C.border}`, fontSize: 10 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 3 }}>
                <span style={{ color: p.reliable ? C.green : C.muted, fontSize: 8, fontWeight: 700 }}>
                  {p.reliable ? '● RELIABLE' : '○ WEAK'}
                </span>
                <span style={{ color: C.gold }}>{p.key}</span>
                <span style={{ color: C.muted }}>×{p.frequency}</span>
                {p.winRate != null && (
                  <span style={{ color: p.winRate >= 0.65 ? C.green : p.winRate <= 0.40 ? C.red : C.amber, fontFamily: C.mono }}>
                    {(p.winRate * 100).toFixed(0)}% wr
                  </span>
                )}
              </div>
              <div style={{ color: '#5A7FA8', fontSize: 9 }}>{p.summary}</div>
            </div>
          ))}
        </div>
      )}

      {/* Operator guidance */}
      <div style={{ padding: '12px 14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
        <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 10 }}>OPERATOR GUIDANCE</div>
        {intel.guidance.trustMore.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 9, color: C.green, marginBottom: 4, fontWeight: 700 }}>TRUST MORE</div>
            {intel.guidance.trustMore.map((t, i) => <div key={i} style={{ fontSize: 10, color: C.text, paddingLeft: 8, marginBottom: 2 }}>› {t}</div>)}
          </div>
        )}
        {intel.guidance.trustLess.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 9, color: C.red, marginBottom: 4, fontWeight: 700 }}>TRUST LESS</div>
            {intel.guidance.trustLess.map((t, i) => <div key={i} style={{ fontSize: 10, color: C.text, paddingLeft: 8, marginBottom: 2 }}>› {t}</div>)}
          </div>
        )}
        {intel.guidance.watchFor.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 9, color: C.amber, marginBottom: 4, fontWeight: 700 }}>WATCH FOR</div>
            {intel.guidance.watchFor.map((t, i) => <div key={i} style={{ fontSize: 10, color: C.text, paddingLeft: 8, marginBottom: 2 }}>› {t}</div>)}
          </div>
        )}
        {intel.guidance.calibNotes.length > 0 && (
          <div>
            <div style={{ fontSize: 9, color: C.dim, marginBottom: 4, fontWeight: 700 }}>CALIBRATION</div>
            {intel.guidance.calibNotes.map((t, i) => <div key={i} style={{ fontSize: 10, color: '#5A7FA8', paddingLeft: 8, marginBottom: 2 }}>› {t}</div>)}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Coaching tab ─────────────────────────────────────────────────────────────

function trendColor(dir: string): string {
  return dir === 'improving' ? C.green : dir === 'degrading' ? C.red : C.dim;
}

function CoachingTab({ report }: { report: CoachingReport }) {
  const insufficient = report.sessionCount < 2;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 760 }}>
      {insufficient && (
        <div style={{ padding: '12px 14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
          <div style={{ fontSize: 10, color: C.muted }}>{report.guidance.notes[0] ?? 'Accumulate sessions to generate coaching insights.'}</div>
        </div>
      )}

      {/* Performance Trends */}
      {report.trends.length > 0 && (
        <div style={{ padding: '12px 14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
          <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 10 }}>PERFORMANCE TRENDS</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {report.trends.map((t, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 10 }}>
                <span style={{ color: C.muted, minWidth: 110 }}>{t.metric.replace(/([A-Z])/g, ' $1').toLowerCase()}</span>
                <span style={{ color: trendColor(t.direction), fontWeight: 700, textTransform: 'uppercase', minWidth: 75 }}>
                  {t.direction}
                </span>
                <span style={{ color: C.text, fontFamily: C.mono, minWidth: 50 }}>
                  {t.from} → {t.to}
                </span>
                <span style={{ color: t.delta >= 0 ? C.green : C.red, fontFamily: C.mono }}>
                  {t.delta >= 0 ? '+' : ''}{t.delta}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Strengths & Weaknesses */}
      {(report.strengths.length > 0 || report.weaknesses.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {[
            { label: 'STRENGTHS', items: report.strengths, accent: C.green },
            { label: 'WEAKNESSES', items: report.weaknesses, accent: C.red },
          ].map(({ label, items, accent }) => (
            <div key={label} style={{ padding: '12px', background: C.card, border: `1px solid ${accent}30`, borderRadius: 3 }}>
              <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 8 }}>{label}</div>
              {items.length === 0
                ? <div style={{ fontSize: 10, color: C.muted }}>None identified yet</div>
                : items.map((s, i) => (
                  <div key={i} style={{ fontSize: 10, color: C.text, marginBottom: 4, lineHeight: 1.5 }}>
                    <span style={{ color: accent, fontWeight: 700 }}>›</span> {s.summary}
                  </div>
                ))}
            </div>
          ))}
        </div>
      )}

      {/* Mistake Patterns */}
      {report.mistakePatterns.length > 0 && (
        <div style={{ padding: '12px 14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
          <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 10 }}>MISTAKE PATTERNS</div>
          {report.mistakePatterns.map((m, i) => {
            const sevColor = m.severity === 'high' ? C.red : m.severity === 'medium' ? C.amber : C.muted;
            return (
              <div key={i} style={{ padding: '8px 10px', marginBottom: 6, background: 'rgba(10,22,40,0.4)', border: `1px solid ${sevColor}30`, borderLeft: `2px solid ${sevColor}`, borderRadius: 3 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ color: sevColor, fontSize: 9, fontWeight: 700, textTransform: 'uppercase' }}>{m.severity}</span>
                  <span style={{ color: C.gold, fontSize: 10 }}>{m.key.replace(/_/g, ' ')}</span>
                  <span style={{ color: C.muted, fontSize: 9 }}>×{m.occurrences} ({(m.rate * 100).toFixed(0)}%)</span>
                </div>
                <div style={{ fontSize: 10, color: '#5A7FA8' }}>{m.guidance}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Operator Profile */}
      {report.operatorProfile && (
        <div style={{ padding: '12px 14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
          <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 10 }}>OPERATOR PROFILE</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 10 }}>
            {[
              { label: 'Strongest edge', value: report.operatorProfile.strongestEdge },
              { label: 'Weakest area',   value: report.operatorProfile.weakestArea },
              { label: 'Risk bias',      value: report.operatorProfile.riskBias },
              { label: 'Calibration',    value: report.operatorProfile.calibrationProfile },
            ].map(({ label, value }) => (
              <div key={label}>
                <span style={{ color: C.muted }}>{label}: </span>
                <span style={{ color: C.text, fontWeight: 700 }}>{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Coaching Guidance */}
      <div style={{ padding: '12px 14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
        <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 10 }}>COACHING GUIDANCE</div>
        {[
          { section: 'MAINTAIN',     items: report.guidance.maintain,   color: C.green },
          { section: 'IMPROVE',      items: report.guidance.improve,    color: C.amber },
          { section: 'AVOID',        items: report.guidance.avoid,      color: C.red   },
          { section: 'FOCUS AREAS',  items: report.guidance.focusAreas, color: C.gold  },
        ].filter(s => s.items.length > 0).map(({ section, items, color }) => (
          <div key={section} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 8, letterSpacing: 1.5, color, fontWeight: 700, marginBottom: 4 }}>{section}</div>
            {items.map((item, i) => (
              <div key={i} style={{ fontSize: 10, color: C.text, paddingLeft: 8, marginBottom: 3, lineHeight: 1.5 }}>
                <span style={{ color }}>›</span> {item}
              </div>
            ))}
          </div>
        ))}
        {report.guidance.notes.length > 0 && (
          <div style={{ marginTop: 6, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
            {report.guidance.notes.map((n, i) => (
              <div key={i} style={{ fontSize: 9, color: '#4A6FA5', lineHeight: 1.6 }}>› {n}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Comparative tab ──────────────────────────────────────────────────────────

function ComparativeTab({ report }: { report: ComparativeReport }) {
  const rankCatColor = (cat: string) =>
    cat === 'top' ? C.green : cat === 'above_avg' ? '#86EFAC'
    : cat === 'below_avg' ? C.amber : cat === 'poor' ? C.red : C.dim;

  const conclusionColor = (c: string) =>
    c === 'validated' ? C.green : c === 'not_effective' ? C.red : C.dim;

  if (report.sessionCount < 2) return (
    <div style={{ color: C.muted, fontSize: 11, paddingTop: 40, textAlign: 'center' }}>
      <div style={{ marginBottom: 6 }}>{report.guidance.notes[0]}</div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 760 }}>
      {/* Session Rankings */}
      {report.rankings.length > 0 && (
        <div style={{ padding: '12px 14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
          <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 10 }}>SESSION RANKINGS</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {report.rankings.map((r, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 10,
                padding: '4px 0', borderBottom: `1px solid ${C.border}` }}>
                <span style={{ color: C.dim, fontFamily: C.mono, minWidth: 24 }}>#{r.rank}</span>
                <span style={{ color: rankCatColor(r.category), fontWeight: 700, textTransform: 'uppercase', minWidth: 75 }}>
                  {r.category.replace('_', ' ')}
                </span>
                <span style={{ color: C.text, fontFamily: C.mono, minWidth: 40 }}>{r.score}</span>
                <span style={{ color: C.muted }}>p{r.percentile.toFixed(0)}</span>
                <span style={{ color: '#3A5070', flex: 1, fontSize: 9 }}>{r.sessionId}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Edge Validations */}
      {report.edgeValidations.length > 0 && (
        <div style={{ padding: '12px 14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
          <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 10 }}>EDGE VALIDATION</div>
          {report.edgeValidations.map((ev, i) => (
            <div key={i} style={{ padding: '6px 0', borderBottom: `1px solid ${C.border}`, fontSize: 10 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 3 }}>
                <span style={{ color: conclusionColor(ev.conclusion), fontWeight: 700, minWidth: 90, textTransform: 'uppercase', fontSize: 9 }}>
                  {ev.conclusion.replace('_', ' ')}
                </span>
                <span style={{ color: C.gold }}>{ev.signalFamily}</span>
                <span style={{ color: ev.performance >= 65 ? C.green : ev.performance <= 40 ? C.red : C.amber,
                  fontFamily: C.mono }}>{ev.performance.toFixed(0)}%</span>
                <span style={{ color: C.muted, fontSize: 9 }}>{ev.sampleSize}s {ev.confidence}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Condition Performance */}
      {report.conditionPerformance.length > 0 && (
        <div style={{ padding: '12px 14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
          <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 10 }}>CONDITION PERFORMANCE</div>
          {report.conditionPerformance.map((cp, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 10,
              padding: '4px 0', borderBottom: `1px solid ${C.border}` }}>
              <span style={{ color: C.gold, minWidth: 100 }}>{cp.condition}</span>
              <span style={{ color: C.text, fontFamily: C.mono, minWidth: 35 }}>{cp.avgScore}</span>
              <span style={{ color: cp.trend === 'improving' ? C.green : cp.trend === 'degrading' ? C.red : C.dim, minWidth: 70 }}>
                {cp.trend}
              </span>
              <span style={{ color: C.muted }}>{cp.sessions} sessions</span>
            </div>
          ))}
        </div>
      )}

      {/* Comparative Guidance */}
      <div style={{ padding: '12px 14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
        <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 10 }}>COMPARATIVE GUIDANCE</div>
        {[
          { section: 'VALIDATED EDGES', items: report.guidance.validatedEdges, color: C.green },
          { section: 'WEAK EDGES',      items: report.guidance.weakEdges,      color: C.red   },
          { section: 'BEST CONDITIONS', items: report.guidance.bestConditions, color: C.gold  },
          { section: 'AVOID',           items: report.guidance.avoidConditions,color: C.amber },
        ].filter(s => s.items.length > 0).map(({ section, items, color }) => (
          <div key={section} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 8, letterSpacing: 1.5, color, fontWeight: 700, marginBottom: 4 }}>{section}</div>
            {items.map((item, i) => (
              <div key={i} style={{ fontSize: 10, color: C.text, paddingLeft: 8, marginBottom: 2 }}>
                <span style={{ color }}>›</span> {item}
              </div>
            ))}
          </div>
        ))}
        {report.guidance.notes.map((n, i) => (
          <div key={i} style={{ fontSize: 9, color: '#4A6FA5', marginTop: 4 }}>› {n}</div>
        ))}
      </div>
    </div>
  );
}

// ── Governance control panel ─────────────────────────────────────────────────

function HealthBadge({ status }: { status: string }) {
  const color = status === 'healthy' ? C.green : status === 'warning' ? C.amber : C.red;
  return (
    <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
      color, border: `1px solid ${color}40`, borderRadius: 2, padding: '1px 5px' }}>
      {status}
    </span>
  );
}

function GovernancePanel({ snap, onRefresh }: { snap: GovernanceSnapshot; onRefresh: () => void }) {
  const modeColor = snap.mode === 'frozen' ? C.red : snap.mode === 'active' ? C.green : C.gold;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Mode + health header */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
        padding: '12px 14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
        <span style={{ fontSize: 9, letterSpacing: 2, color: C.dim }}>MODE</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: modeColor, textTransform: 'uppercase' }}>
          {snap.mode}
        </span>
        <HealthBadge status={snap.health.status} />
        {snap.frozenReason && (
          <span style={{ fontSize: 9, color: C.red, flex: 1 }}>{snap.frozenReason}</span>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {/* Mode controls */}
          {snap.mode !== 'frozen' && snap.mode !== 'advisory' && (
            <button onClick={() => { setGovernanceMode('advisory'); onRefresh(); }} style={{
              padding: '3px 8px', fontSize: 9, cursor: 'pointer', fontFamily: C.mono,
              background: 'none', border: `1px solid ${C.amber}`, borderRadius: 2, color: C.amber,
            }}>→ ADVISORY</button>
          )}
          {snap.mode !== 'frozen' && (
            <button onClick={() => { freeze('Manually frozen by operator'); onRefresh(); }} style={{
              padding: '3px 8px', fontSize: 9, cursor: 'pointer', fontFamily: C.mono,
              background: 'none', border: `1px solid ${C.red}`, borderRadius: 2, color: C.red,
            }}>⊘ FREEZE</button>
          )}
          {snap.mode === 'frozen' && (
            <button onClick={() => { unfreeze('advisory'); onRefresh(); }} style={{
              padding: '3px 8px', fontSize: 9, cursor: 'pointer', fontFamily: C.mono,
              background: 'none', border: `1px solid ${C.green}`, borderRadius: 2, color: C.green,
            }}>✓ UNFREEZE</button>
          )}
        </div>
      </div>

      {/* Health warnings */}
      {snap.health.warnings.length > 0 && (
        <div style={{ padding: '8px 12px', background: 'rgba(252,211,77,0.06)', border: `1px solid ${C.amber}40`, borderRadius: 3 }}>
          {snap.health.warnings.map((w, i) => (
            <div key={i} style={{ fontSize: 10, color: C.amber }}>⚠ {w}</div>
          ))}
        </div>
      )}

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
        {[
          { label: 'PROPOSALS', value: snap.stats.totalProposals },
          { label: 'APPLIED',   value: snap.stats.appliedCount },
          { label: 'REJECTED',  value: snap.stats.rejectedCount },
          { label: 'ROLLED BACK', value: snap.stats.rolledBackCount },
        ].map(m => (
          <div key={m.label} style={{ padding: '8px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3, textAlign: 'center' }}>
            <div style={{ fontSize: 8, letterSpacing: 1, color: C.dim, marginBottom: 3 }}>{m.label}</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text, fontFamily: C.mono }}>{m.value}</div>
          </div>
        ))}
      </div>

      {/* Pending proposals */}
      {snap.proposals.filter(p => p.status === 'pending').length > 0 && (
        <div style={{ padding: '12px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
          <div style={{ fontSize: 9, letterSpacing: 2, color: C.amber, marginBottom: 8 }}>
            PENDING PROPOSALS ({snap.proposals.filter(p => p.status === 'pending').length})
          </div>
          {snap.proposals.filter(p => p.status === 'pending').map((p, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 10,
              padding: '6px 0', borderBottom: `1px solid ${C.border}` }}>
              <span style={{ color: C.dim, minWidth: 80, fontFamily: C.mono }}>{p.id}</span>
              <span style={{ color: C.gold, minWidth: 70, fontWeight: 700 }}>{p.signalKey}</span>
              <span style={{ color: C.muted, fontFamily: C.mono }}>
                {p.currentModifier.toFixed(2)} → {p.proposedModifier.toFixed(2)}
              </span>
              <span style={{ color: p.delta > 0 ? C.green : C.red, fontFamily: C.mono, minWidth: 50 }}>
                {p.delta > 0 ? '+' : ''}{p.delta.toFixed(3)}
              </span>
              <span style={{ color: '#5A7FA8', flex: 1, fontSize: 9 }}>{p.reason}</span>
              <button onClick={() => { approveProposal(p.id); onRefresh(); }} style={{
                padding: '2px 6px', fontSize: 9, cursor: 'pointer', fontFamily: C.mono,
                background: 'none', border: `1px solid ${C.green}`, borderRadius: 2, color: C.green,
              }}>✓</button>
              <button onClick={() => { rejectProposal(p.id); onRefresh(); }} style={{
                padding: '2px 6px', fontSize: 9, cursor: 'pointer', fontFamily: C.mono,
                background: 'none', border: `1px solid ${C.red}`, borderRadius: 2, color: C.red,
              }}>✕</button>
            </div>
          ))}
        </div>
      )}

      {/* Current modifiers */}
      {Object.keys(snap.currentModifiers).length > 0 && (
        <div style={{ padding: '12px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
          <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 8 }}>ACTIVE MODIFIERS</div>
          {Object.entries(snap.currentModifiers).map(([sig, mod], i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 10,
              padding: '4px 0', borderBottom: `1px solid ${C.border}` }}>
              <span style={{ color: C.gold, minWidth: 90 }}>{sig}</span>
              <span style={{ color: mod > 1 ? C.green : mod < 1 ? C.red : C.dim, fontFamily: C.mono, fontWeight: 700 }}>
                ×{mod.toFixed(3)}
              </span>
              <button onClick={() => { rollbackSignal(sig); onRefresh(); }} style={{
                marginLeft: 'auto', padding: '2px 6px', fontSize: 9, cursor: 'pointer',
                fontFamily: C.mono, background: 'none', border: `1px solid ${C.muted}`,
                borderRadius: 2, color: C.muted,
              }}>↩ revert</button>
            </div>
          ))}
          {/* Rollback actions */}
          <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
            <button onClick={() => { rollbackLast(); onRefresh(); }} style={{
              padding: '4px 10px', fontSize: 9, cursor: 'pointer', fontFamily: C.mono,
              background: 'none', border: `1px solid ${C.muted}`, borderRadius: 2, color: C.muted,
            }}>↩ ROLLBACK LAST</button>
            <button onClick={() => { rollbackAll(); onRefresh(); }} style={{
              padding: '4px 10px', fontSize: 9, cursor: 'pointer', fontFamily: C.mono,
              background: 'none', border: `1px solid ${C.red}40`, borderRadius: 2, color: C.red,
            }}>⊗ ROLLBACK ALL</button>
          </div>
        </div>
      )}

      {/* Ledger (last 10 entries) */}
      {snap.ledger.length > 0 && (
        <div style={{ padding: '12px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
          <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 8 }}>
            ADAPTATION LEDGER (last {Math.min(10, snap.ledger.length)} of {snap.ledger.length})
          </div>
          {[...snap.ledger].reverse().slice(0, 10).map((e, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 9,
              padding: '4px 0', borderBottom: `1px solid ${C.border}`, fontFamily: C.mono }}>
              <span style={{ color: C.muted, minWidth: 35 }}>#{e.seq}</span>
              <span style={{ color: e.applied ? C.green : C.muted, minWidth: 8 }}>
                {e.applied ? '●' : '○'}
              </span>
              <span style={{ color: C.gold, minWidth: 80 }}>{e.signalKey}</span>
              <span style={{ color: C.muted }}>{e.previousModifier.toFixed(3)} → {e.newModifier.toFixed(3)}</span>
              <span style={{ color: '#3A5070', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {e.reason.slice(0, 60)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Approval toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
        background: C.card, border: `1px solid ${C.border}`, borderRadius: 3, fontSize: 10 }}>
        <span style={{ color: C.dim }}>Require approval for proposals:</span>
        <button onClick={() => { setRequireApproval(!snap.requireApproval); onRefresh(); }} style={{
          padding: '3px 10px', fontSize: 9, cursor: 'pointer', fontFamily: C.mono,
          background: snap.requireApproval ? 'rgba(74,222,128,0.1)' : 'rgba(248,113,113,0.1)',
          border: `1px solid ${snap.requireApproval ? C.green : C.red}`,
          borderRadius: 2, color: snap.requireApproval ? C.green : C.red, fontWeight: 700,
        }}>
          {snap.requireApproval ? 'ON' : 'OFF'}
        </button>
        <span style={{ color: C.muted, fontSize: 9 }}>
          {snap.requireApproval ? 'Proposals require manual approval' : 'Proposals auto-apply'}
        </span>
      </div>
    </div>
  );
}

// ── Learning tab ─────────────────────────────────────────────────────────────

function tierColor(tier: string): string {
  return tier === 'high' ? C.green : tier === 'moderate' ? C.amber : tier === 'low' ? C.red : C.muted;
}

function LearningTab({ profile, appliedState }: { profile: AdaptiveLearningProfile; appliedState: AppliedModifier[] }) {
  if (!profile.meta.sufficientData) return (
    <div style={{ color: C.muted, fontSize: 11, paddingTop: 40, textAlign: 'center' }}>
      <div style={{ marginBottom: 8 }}>Adaptive learning requires ≥{AL.MIN_SAMPLE_SIZE} evaluated snapshots.</div>
      <div style={{ fontSize: 10 }}>Current: {profile.evaluationCount} snapshot{profile.evaluationCount !== 1 ? 's' : ''}. Mark outcomes as worked/failed to build the learning profile.</div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 760 }}>
      {/* Header */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '10px 14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
        <span style={{ fontSize: 9, letterSpacing: 2, color: C.dim }}>MODE</span>
        <span style={{ fontSize: 10, fontWeight: 700, color: C.gold, textTransform: 'uppercase', letterSpacing: 1 }}>{profile.mode}</span>
        {/* Active adaptation status indicator */}
        {profile.mode === 'active' && (
          <span style={{
            fontSize: 8, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
            color: isActiveAdaptationEnabled() ? C.green : C.muted,
            border: `1px solid ${isActiveAdaptationEnabled() ? C.green : C.muted}40`,
            borderRadius: 2, padding: '1px 5px',
          }}>
            {isActiveAdaptationEnabled() ? 'ACTIVE — MODIFIERS ON' : 'ACTIVE — FLAG OFF (advisory)'}
          </span>
        )}
        <span style={{ color: C.border }}>|</span>
        <span style={{ fontSize: 9, letterSpacing: 2, color: C.dim }}>SAMPLES</span>
        <span style={{ fontSize: 10, color: C.text, fontFamily: C.mono }}>{profile.evaluationCount}</span>
      </div>

      {/* Active application state — shows what is proposed/applied and to what target */}
      {appliedState.length > 0 && (
        <div style={{ padding: '14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
          <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 10 }}>
            PROPOSED MODIFIER STATE ({appliedState.length} signal{appliedState.length > 1 ? 's' : ''})
            {isActiveAdaptationEnabled()
              ? <span style={{ color: C.green, marginLeft: 8 }}>● ACTIVE — MODIFIERS APPLYING</span>
              : <span style={{ color: C.muted, marginLeft: 8 }}>○ ADVISORY — NOT APPLIED</span>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {appliedState.map((m, i) => {
              const modColor = m.learnedModifier > 1 ? C.green : m.learnedModifier < 1 ? C.red : C.dim;
              return (
                <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 10,
                  padding: '6px 10px', background: 'rgba(10,22,40,0.4)', border: `1px solid ${C.border}`, borderRadius: 3 }}>
                  <span style={{ color: C.dim, fontSize: 8, letterSpacing: 1, minWidth: 70, textTransform: 'uppercase' }}>{m.target}</span>
                  <span style={{ color: C.gold, minWidth: 80, fontWeight: 700 }}>{m.signalKey}</span>
                  <span style={{ color: modColor, fontFamily: C.mono, minWidth: 45, fontWeight: 700 }}>×{m.learnedModifier.toFixed(3)}</span>
                  <span style={{ color: C.muted, flex: 1 }}>{m.reason}</span>
                  <span style={{ color: C.dim, fontSize: 9 }}>{m.sampleSize}s</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Signal reliability */}
      {profile.reliability.length > 0 && (
        <div style={{ padding: '14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
          <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 10 }}>SIGNAL RELIABILITY</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {profile.reliability.map((r, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 10, padding: '4px 0', borderBottom: `1px solid ${C.border}` }}>
                <span style={{ color: tierColor(r.reliabilityTier), minWidth: 80, fontWeight: 700, textTransform: 'uppercase' }}>{r.signalKey}</span>
                <span style={{ color: C.muted, minWidth: 55 }}>{r.sampleSize} sample{r.sampleSize !== 1 ? 's' : ''}</span>
                <span style={{ color: r.accuracyRate !== null && r.accuracyRate >= AL.HIGH_RELIABILITY_MIN ? C.green : r.accuracyRate !== null && r.accuracyRate <= AL.LOW_RELIABILITY_MAX ? C.red : C.amber, fontFamily: C.mono, minWidth: 45 }}>
                  {r.accuracyRate !== null ? `${(r.accuracyRate * 100).toFixed(0)}%` : '—'}
                </span>
                <span style={{ color: tierColor(r.reliabilityTier), fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5 }}>{r.reliabilityTier}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Weight recommendations */}
      {profile.weightProfiles.length > 0 && (
        <div style={{ padding: '14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
          <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 10 }}>ADAPTIVE WEIGHT RECOMMENDATIONS</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {profile.weightProfiles.map((w, i) => {
              const modColor = w.learnedModifier > 1 ? C.green : w.learnedModifier < 1 ? C.red : C.dim;
              return (
                <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 10, padding: '6px 10px', background: 'rgba(10,22,40,0.4)', border: `1px solid ${C.border}`, borderRadius: 3 }}>
                  <span style={{ color: C.gold, minWidth: 80, fontWeight: 700, textTransform: 'uppercase' }}>{w.layer}</span>
                  <span style={{ color: modColor, fontFamily: C.mono, minWidth: 40, fontWeight: 700 }}>×{w.learnedModifier.toFixed(2)}</span>
                  <span style={{ color: C.muted, flex: 1 }}>{w.reason}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Calibration profile */}
      {profile.calibrationProfiles.map((cp, i) => cp.confidenceBias !== 'insufficient_data' && (
        <div key={i} style={{ padding: '14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
          <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 8 }}>CALIBRATION PROFILE</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: calibColor(cp.confidenceBias), textTransform: 'uppercase' }}>
              {cp.confidenceBias.replace('_', ' ')}
            </span>
            {cp.suggestedAdjustment !== null && (
              <span style={{ fontSize: 10, color: C.amber }}>
                Suggested: ×{cp.suggestedAdjustment.toFixed(2)} confidence multiplier
              </span>
            )}
          </div>
          <div style={{ fontSize: 10, color: '#5A7FA8' }}>{cp.explanation}</div>
        </div>
      ))}

      {/* Regime stats */}
      {profile.regimeStats.length > 0 && (
        <div style={{ padding: '14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
          <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 10 }}>REGIME-CONDITIONED PERFORMANCE</div>
          {profile.regimeStats.map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, fontSize: 10, marginBottom: 4 }}>
              <span style={{ color: C.text, minWidth: 100, textTransform: 'uppercase' }}>{r.regimeKeyStr}</span>
              <span style={{ color: C.muted }}>{r.stats.sampleSize} samples</span>
              <span style={{ color: r.stats.accuracyRate !== null && r.stats.accuracyRate >= AL.HIGH_RELIABILITY_MIN ? C.green : C.red, fontFamily: C.mono }}>
                {r.stats.accuracyRate !== null ? `${(r.stats.accuracyRate * 100).toFixed(0)}%` : '—'}
              </span>
              <span style={{ color: tierColor(r.stats.reliabilityTier) }}>{r.stats.reliabilityTier}</span>
            </div>
          ))}
        </div>
      )}

      {/* Lessons */}
      <div style={{ padding: '14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
        <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 10 }}>ADAPTIVE LESSONS</div>
        {profile.lessons.map((l, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, fontSize: 11, color: C.text, lineHeight: 1.6, marginBottom: 4 }}>
            <span style={{ color: C.gold, flexShrink: 0 }}>›</span><span>{l}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Execution Feedback tab ───────────────────────────────────────────────────

function ExecutionFeedbackTab({
  feedbacks,
  summary,
}: {
  feedbacks: ExecutionFeedback[];
  summary: ReturnType<typeof summarizeExecutionFeedback>;
}) {
  if (feedbacks.length === 0) {
    return (
      <div style={{ color: C.muted, fontSize: 11, paddingTop: 40, textAlign: 'center' }}>
        <div style={{ marginBottom: 8 }}>No execution data available.</div>
        <div style={{ fontSize: 10 }}>Log trades in the Trade Log tab and ensure entries include stop and exit prices to generate feedback.</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 760 }}>
      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
        {[
          { label: 'PLANS EVAL', value: `${summary.count}`, color: C.text },
          { label: 'AVG SCORE',  value: summary.avgScore !== null ? `${summary.avgScore}` : '—', color: summary.avgScore !== null ? scoreColor(summary.avgScore) : C.muted },
          { label: 'FOLLOW RATE', value: summary.followedRate !== null ? `${(summary.followedRate*100).toFixed(0)}%` : '—', color: C.gold },
          { label: 'TARGET RATE', value: summary.targetsHitRate !== null ? `${(summary.targetsHitRate*100).toFixed(0)}%` : '—', color: C.green },
        ].map(m => (
          <div key={m.label} style={{ padding: '10px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3, textAlign: 'center' }}>
            <div style={{ fontSize: 8, letterSpacing: 1.5, color: C.dim, marginBottom: 4 }}>{m.label}</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: m.color, fontFamily: C.mono }}>{m.value}</div>
          </div>
        ))}
      </div>

      {/* Issues and strengths */}
      {(summary.topIssues.length > 0 || summary.topStrengths.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {summary.topIssues.length > 0 && (
            <div style={{ padding: '12px', background: 'rgba(248,113,113,0.05)', border: `1px solid rgba(248,113,113,0.2)`, borderRadius: 3 }}>
              <div style={{ fontSize: 9, letterSpacing: 2, color: C.red, marginBottom: 8 }}>TOP ISSUES</div>
              {summary.topIssues.map((issue, i) => (
                <div key={i} style={{ fontSize: 10, color: C.text, lineHeight: 1.7 }}>⚠ {issue}</div>
              ))}
            </div>
          )}
          {summary.topStrengths.length > 0 && (
            <div style={{ padding: '12px', background: 'rgba(74,222,128,0.05)', border: `1px solid rgba(74,222,128,0.2)`, borderRadius: 3 }}>
              <div style={{ fontSize: 9, letterSpacing: 2, color: C.green, marginBottom: 8 }}>STRENGTHS</div>
              {summary.topStrengths.map((s, i) => (
                <div key={i} style={{ fontSize: 10, color: C.text, lineHeight: 1.7 }}>✓ {s}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Per-feedback detail */}
      {feedbacks.map((fb: ExecutionFeedback, i: number) => (
        <div key={i} style={{ padding: '14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
          {/* Header */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 10, color: C.gold, fontWeight: 700 }}>{fb.planId}</span>
            <span style={{ fontSize: 10, color: fb.comparison.followed ? C.green : C.muted }}>
              {fb.comparison.followed ? '✓ Followed' : '○ Not followed'}
            </span>
            <span style={{ fontSize: 9, color: C.muted, textTransform: 'uppercase' }}>{fb.comparison.outcome.replace('_', ' ')}</span>
            <div style={{ marginLeft: 'auto' }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: scoreColor(fb.score.overall), fontFamily: C.mono }}>
                {fb.score.overall}/100
              </span>
            </div>
          </div>

          {/* Score bars */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginBottom: 10 }}>
            {[
              { label: 'ENTRY', v: fb.score.components.entryQuality },
              { label: 'TIMING', v: fb.score.components.timing },
              { label: 'RISK', v: fb.score.components.adherence },
              { label: 'OUTCOME', v: fb.score.components.outcomeAlignment },
            ].map(m => (
              <div key={m.label} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 7, letterSpacing: 1, color: C.muted, marginBottom: 2 }}>{m.label}</div>
                <div style={{ height: 3, background: '#1A3050', borderRadius: 2, marginBottom: 2 }}>
                  <div style={{ width: `${m.v}%`, height: '100%', background: scoreColor(m.v), borderRadius: 2 }} />
                </div>
                <div style={{ fontSize: 9, color: C.text, fontFamily: C.mono }}>{m.v}</div>
              </div>
            ))}
          </div>

          {/* Entry quality */}
          <div style={{ fontSize: 10, color: '#5A7FA8', marginBottom: 6, lineHeight: 1.6 }}>
            Entry: <span style={{ color: C.text }}>{fb.entry.timing}</span> timing ·{' '}
            <span style={{ color: C.text }}>{fb.entry.priceQuality}</span> fill
            {fb.entry.slippagePct !== null && <span style={{ color: C.muted }}> · {fb.entry.slippagePct}% slippage</span>}
          </div>

          {/* Guidance */}
          {fb.guidance.length > 0 && (
            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 8, marginTop: 4 }}>
              {fb.guidance.map((g, j) => (
                <div key={j} style={{ fontSize: 10, color: C.text, lineHeight: 1.6, display: 'flex', gap: 6 }}>
                  <span style={{ color: C.gold, flexShrink: 0 }}>›</span>
                  <span>{g}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Main workspace ─────────────────────────────────────────────────────────────

type Tab = 'log' | 'audit' | 'attribution' | 'calibration' | 'learning' | 'intelligence' | 'coaching' | 'comparative' | 'execution';

const TABS: { id: Tab; label: string }[] = [
  { id: 'log',         label: 'Trade Log'   },
  { id: 'audit',       label: 'Audit'       },
  { id: 'attribution', label: 'Attribution' },
  { id: 'calibration', label: 'Calibration' },
  { id: 'learning',    label: 'Learning'    },
  { id: 'intelligence', label: 'Intelligence' },
  { id: 'coaching',     label: 'Coaching'     },
  { id: 'comparative',  label: 'Comparative'  },
  { id: 'execution',    label: 'Execution'    },
];

export function Journal() {
  const { activeSymbol, journal, symbolData, settings, weights } = useStore();
  const [tab, setTab] = useState<Tab>('log');
  const sym = activeSymbol ?? '';

  // Load snapshots and run outcome evaluation — deterministic from stored data
  const { evaluations, summary } = useMemo(() => {
    const snapshots = sym ? loadSnapshotsForSymbol(sym) : [];
    const journalEntries = journal ?? [];
    return evaluateSession(sym, snapshots, journalEntries);
  }, [sym, journal]);

  // Adaptive learning profile — deterministic from evaluations
  const learningProfile: AdaptiveLearningProfile = useMemo(
    () => buildAdaptiveLearningProfile(sym, evaluations, 'advisory'),
    [sym, evaluations],
  );

  // Session intelligence summary — operator-facing scoring, grading, patterns, guidance
  const sessionIntelligence: SessionIntelligenceSummary = useMemo(() => {
    const replayOutput = analyseReplayHistory(sym, []);   // thesis history path
    return buildSessionIntelligenceSummary(sym, evaluations, summary, replayOutput, learningProfile);
  }, [sym, evaluations, summary, learningProfile]);

  // Coaching report — multi-session analysis
  const coachingReport: CoachingReport = useMemo(
    () => buildCoachingReport(sym, [sessionIntelligence]),
    [sym, sessionIntelligence],
  );

  // Comparative Intelligence Report — session ranking, edge validation, condition performance
  const comparativeReport: ComparativeReport = useMemo(
    () => buildComparativeReport(sym, [sessionIntelligence], coachingReport),
    [sym, sessionIntelligence, coachingReport],
  );

  // Build structured applied-modifier state — the canonical state governance manages.
  // This is a pure read: it does NOT apply anything, just shows what WOULD be applied.
  const nullRegime = React.useMemo(
    () => ({ volatility: 'unknown', gamma: 'unknown', thesis: 'unknown', trap: null as null }),
    [],
  );
  const appliedModifierState: AppliedModifier[] = useMemo(
    () => buildAppliedModifierState(
      learningProfile.weightProfiles,
      learningProfile.calibrationProfiles,
      nullRegime,
    ),
    [learningProfile.weightProfiles, learningProfile.calibrationProfiles, nullRegime],
  );

  // Wire learning recommendations into governance proposals (advisory or active).
  // This connects the learning layer output to the governance approval flow.
  // Only runs when there are non-neutral weight profiles to propose.
  React.useEffect(() => {
    if (learningProfile.weightProfiles.length === 0) return;
    proposeFromLearningProfile(learningProfile, nullRegime);
    refreshGov();
  // Only re-propose when the learning profile meaningfully changes (evaluationCount is stable proxy)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learningProfile.evaluationCount]);

  // NOTE: Active adaptation audit recording is now handled by the canonical shared seam
  // (useAdaptiveSignalsVM in Signals workspace). Journal is a governance state consumer,
  // not the adaptation hub. Proposal flow (below) remains here.

  // Execution feedback — evaluate most recent plan against linked journal entries
  const execCtx    = sym ? getSymbolContext(sym, symbolData[sym], settings, weights) : null;
  const execThesis = sym ? getThesis(sym)?.current ?? null : null;
  const execPlanOut = useMemo(
    () => execCtx ? buildExecutionPlan(execCtx, execThesis) : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sym, execCtx?.curPrice, execThesis?.inputHash],
  );
  const executionFeedbacks: ExecutionFeedback[] = useMemo(() => {
    if (!execPlanOut?.primaryPlan) return [];
    const plan = execPlanOut.primaryPlan;
    // Match journal entries for this symbol
    const symEntries = (journal ?? []).filter(je => je.symbol === sym).slice(-5);
    return symEntries.map(je => evaluateExecution(plan, je));
  }, [execPlanOut, journal, sym]);
  const execSummary = useMemo(
    () => summarizeExecutionFeedback(executionFeedbacks),
    [executionFeedbacks],
  );

  // Governance snapshot — refreshed on every re-render tick that changes governance state
  const [govVersion, setGovVersion] = React.useState(0);
  const govSnap: GovernanceSnapshot = useMemo(
    () => getGovernanceSnapshot(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [govVersion],
  );
  const refreshGov = React.useCallback(() => setGovVersion(v => v + 1), []);

  const actedOnCount = evaluations.filter(e =>
    e.decisionStatus === 'worked' || e.decisionStatus === 'failed' || e.decisionStatus === 'acted_on'
  ).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: '1 1 0%', fontFamily: C.mono }}>
      <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '8px 18px', background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 10, letterSpacing: 1.5, fontFamily: 'inherit',
            color: tab === t.id ? C.gold : C.muted,
            borderBottom: tab === t.id ? `2px solid ${C.gold}` : '2px solid transparent',
          }}>
            {t.label.toUpperCase()}
          </button>
        ))}
        {/* Acted-on count badge */}
        {actedOnCount > 0 && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', paddingRight: 14 }}>
            <span style={{ fontSize: 9, color: C.amber, border: `1px solid ${C.amber}40`, borderRadius: 2, padding: '1px 5px' }}>
              {actedOnCount} evaluated
            </span>
          </div>
        )}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {tab === 'log'         && <JournalPanel activeSymbol={sym} />}
        {tab === 'audit'       && <AuditPanel />}
        {tab === 'attribution' && <AttributionTab evaluations={evaluations} summary={summary} />}
        {tab === 'calibration' && <CalibrationTab evaluations={evaluations} summary={summary} />}
        {tab === 'intelligence' && <IntelligenceTab intel={sessionIntelligence} />}
        {tab === 'coaching'     && <CoachingTab report={coachingReport} />}
        {tab === 'comparative' && <ComparativeTab report={comparativeReport} />}
        {tab === 'execution'   && <ExecutionFeedbackTab feedbacks={executionFeedbacks} summary={execSummary} />}
        {tab === 'learning'     && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 760 }}>
            <GovernancePanel snap={govSnap} onRefresh={refreshGov} />
            <LearningTab profile={learningProfile} appliedState={appliedModifierState} />
          </div>
        )}
      </div>
    </div>
  );
}
